
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import dayjs from 'dayjs';
import config from '@/config';
import { formRules, createExternalAccountIdRule } from '@/services/userSpace/UserService';
import { createUser } from '@/api/resources/ssoApi';
import { PlatformKey, SERVICE_TOOLTIP_IMAGES, ServiceKey } from '@/constants';
import { getTranslation } from '@/i18n';
import { getCookieConsentements } from '@/libs/cookies-consentment';
import { loginAndRedirectToLanding } from '@/services/authSpace/sso.service';
import { AgeRestriction, getUIConfig } from '@/configs';
import SSOPartnerSynchronizeDialog from '@/components/Dialogs/SSOPartnerSynchronizeDialog.vue';
import AskSSOSynchroDialog from '@/components/Dialogs/AskSSOSynchroDialog.vue'; // EXTERNAL_AND_DOB_EXCEPTIONS
import { getApiErrorCode } from '@/api/utils';
import SSOPartnerErrorDialog from '../Dialogs/SSOPartnerErrorDialog.vue';

// EXTERNAL_AND_DOB_EXCEPTIONS
const PAIRING_NOT_FOUND_EXCEPTION = 'PairingNotFoundException';
const CONFLICT = 'Conflict';
const NON_UNIQUE_EXTERNAL_ACCOUNT_ID = 'NonUniqueExternalAccountID';

// EXTERNAL_EXCEPTIONS
const CARD_FORMAT_EXCEPTION = 'CardFormatException';
const IRIGO_USER_NOT_FOUND = 'IrigoUserNotFound';
const IRIGO_MTICKET_USER_NOT_FOUND = 'IrigoMTicketUserNotFound';
const BIBUS_MTICKET_USER_NOT_FOUND = 'BibusMTicketUserNotFound';
const KORRIGO_USER_NOT_FOUND = 'KorrigoUserNotFound';
const EXTERNAL_API_DOWN = 'ErrorContactingExternalAPI';
const CONFLICT_ASTUCE = 'AstuceExternalAccountIdConflict';
const CONFLICT_SISMO = 'SISMOIdConflict';
const TBM_UNAUTHORIZED_EXCEPTION = 'UnauthorizedTBMApiException';
const TBM_PAIRING_EXCEPTION = 'TBMPairingException';
const TBM_MTICKET_PAIRING_EXCEPTION = 'TBMMticketPairingException';
const TBM_CUSTOMER_NOT_FOUND_EXCEPTION = 'TBMCustomerNotFoundException';
const TBM_SVD_DIFFERENCE_DETECTED_EXCEPTION = 'TBMSVDDifferenceDetectedException';
const TBM_SVD_CUSTOMER_NOT_FOUND = 'TBMSVDCustomerNotFound';
const TBM_CONFLICT_EXCEPTION = 'TBMConflictException';
const MODALIS_CONFLICT_EXCEPTION = 'ModalisConflictException';
const MATAWAN_USER_NOT_FOUND = 'MatawanUserNotFound';
const PASS_PASS_DOB_MISMATCH = 'PassPassDOBMismatchException';
const PASS_PASS_API_MISMATCH = 'PassPassPartnerAPIException';
const TAO_DOB_MISMATCH = 'TaoDOBMismatchException';

const PARTNER_UPDATE_EXCEPTION = 'PartnerUpdateException';
const EXTERNAL_AND_DOB_EXCEPTIONS = [PAIRING_NOT_FOUND_EXCEPTION, CONFLICT, NON_UNIQUE_EXTERNAL_ACCOUNT_ID];
const EXTERNAL_EXCEPTIONS = [CARD_FORMAT_EXCEPTION];
const CLIENT_EXCEPTIONS = [PARTNER_UPDATE_EXCEPTION];

interface Tooltip {
  mainDesc: string;
  imageSrc: string;
  optionalDesc: string;
}

@Component({
  components: { AskSSOSynchroDialog, SSOPartnerSynchronizeDialog, SSOPartnerErrorDialog },
  beforeRouteEnter(to, from, next) {
    if (!to.query.userInfos) {
      next({ name: 'sso:signin' });
    } else {
      next();
    }
  },
})
export default class SSOPairingForm extends Vue {
  @Prop({ type: String, required: true }) userInfos!: string;

  @Prop({ type: String, required: false }) externalAccountIdFromSSO!: string;

  @Prop({ type: String, required: false }) externalAccessToken!: string;

  @Prop({
    type: Array,
    required: false,
    default: () => [],
  })
  relatedTicketings!: ('MTICKET' | 'SVD')[];

  formRules!: typeof formRules;

  form = {
    referrerCode: '',
    dateOfBirth: undefined,
    service: undefined,
    newsletterIsAccepted: false,
    cguValidated: false,
    externalAccountId: undefined,
  };

  submittedForm: any = null;

  state: 'signup' | 'need-external-account' | 'need-external-account-optional' = 'signup';

  regex: RegExp | undefined = undefined;

  submitting: boolean = false;

  errorCode: string = '';

  showDialogError: boolean = false;

  userInput: any = {};

  cardTooltip: Tooltip = {
    mainDesc: '',
    imageSrc: '',
    optionalDesc: '',
  };

  showCardTooltip: boolean = false;

  underAgeError: boolean = false;

  underAgeErrorMessage: string = 'L\'inscription est impossible pour les personnes de moins de 8 ans.';

  // ===== lifecycle =======
  created() {
    this.formRules = formRules;

    const [firstService] = this.sortedServices;
    this.form.service = firstService;
    this.updateTooltipImage(firstService.key);

    this.$watch(((vm) => [vm.form.service, vm.errorCode]) as any, () => this.determineState(), { immediate: true });
  }

  @Watch('form.service')
  resetExternalAccountId() {
    this.form.externalAccountId = undefined;
  }

  @Watch('errorCode')
  onErrorCodeChange() {
    this.showDialogError = this.errorCode === CONFLICT_ASTUCE || this.errorCode === CONFLICT_SISMO;
  }

  // ===== watchers =======
  // TODO: If form services really differs that much in the future, remove determineState and the watchers
  // to use KeepAlive component
  determineState() {
    const service = this.form.service as any;
    if (!service) {
      return;
    }

    this.regex = undefined;

    const stateByService: Record<
      | ServiceKey.IRIGO_ATOUT
      | ServiceKey.MODALIS
      | ServiceKey.TBM
      | ServiceKey.AVANTAG
      | ServiceKey.KORRIGO_BREST
      | ServiceKey.TBM_MTICKET
      | ServiceKey.IZILO
      | ServiceKey.TUL
      | ServiceKey.OISE
      | ServiceKey.TWISTO
      | ServiceKey.TWISTO_MTICKET
      | ServiceKey.TAO,
      () => void
    > = {
      [ServiceKey.IRIGO_ATOUT]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.MODALIS]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.TBM]: () => {
        if (!this.relatedTicketings.includes('SVD')) {
          this.state = 'need-external-account';
        } else {
          this.state = 'signup';
        }
      },
      [ServiceKey.TBM_MTICKET]: () => {
        this.state = 'signup';
      },
      [ServiceKey.AVANTAG]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.KORRIGO_BREST]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.IZILO]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.TUL]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.OISE]: () => {
        this.state = 'need-external-account-optional';
      },
      [ServiceKey.TWISTO]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.TWISTO_MTICKET]: () => {
        this.state = 'need-external-account';
      },
      [ServiceKey.TAO]: () => {
        this.state = 'need-external-account';
        this.regex = this.platform.config.externalAccountIdRegex;
      },
    };

    if (stateByService[service.key]) {
      stateByService[service.key]();
    } else {
      this.state = 'signup';
    }
  }

  @Watch('form.service')
  onServiceChange() {
    this.errorCode = '';
  }

  updateTooltipImage(serviceKey: ServiceKey) {
    const serviceTooltip = SERVICE_TOOLTIP_IMAGES[serviceKey] || {};
    this.cardTooltip = {
      mainDesc: serviceTooltip.mainDesc || this.platform.style.signup.cardTooltipMainDesc,
      imageSrc: serviceTooltip.imageSrc || this.platform.style.signup.cardTooltipImageUrl,
      optionalDesc: serviceTooltip.optionalDesc || this.platform.style.signup.cardTooltipOptionalDesc,
    };
  }

  // ===== computed =======
  get ssoOptions() {
    return this.$store.getters['platform/ssoOptions'];
  }

  get platform() {
    return this.$store.getters['platform/getPlatform'];
  }

  get platformName() {
    return this.platform.name;
  }

  // TODO: Remove this getter when more platforms support serial number or similar feature.
  get isBKlubPlatform() {
    return this.platform.key === PlatformKey.BKLUB;
  }

  get isTBMPlatform() {
    return this.platform.key === PlatformKey.TBM;
  }

  get sortedServices() {
    return this.platform.platformService
      .filter((platformService) => platformService.forSignup)
      .sort((a, z) => a.rank - z.rank)
      .map((service) => service.service);
  }

  get hasServiceToSelect() {
    return this.sortedServices.length > 1;
  }

  get newsletterLabel() {
    return getTranslation(`${this.platform.key}.signUpForm.newsletterLabel`) || getTranslation(`signUpForm.newsletterLabel`);
  }

  get needExternalAccountField() {
    const service = this.form.service as any;
    const targetedKey = service.key; // it can be undefined, it doesnt matter

    return {
      label: getTranslation(`${targetedKey}.signUpForm.needExternalAccountField.label`) || getTranslation(`signUpForm.needExternalAccountField.label`),
      info: getTranslation(`${targetedKey}.signUpForm.needExternalAccountField.info`) || getTranslation(`signUpForm.needExternalAccountField.info`),
      error: getTranslation(`${targetedKey}.signUpForm.needExternalAccountField.error`) || getTranslation(`signUpForm.needExternalAccountField.error`),
    };
  }

  get walletNumberField() {
    return {
      label: getTranslation('signUpForm.walletNumberField.label'),
      info: getTranslation('signUpForm.walletNumberField.info'),
      error: getTranslation('signUpForm.walletNumberField.error'),
    };
  }

  get externalAccountIdRules() {
    if (this.regex) {
      return createExternalAccountIdRule(this.regex);
    }
    return [(v) => !!v || this.needExternalAccountField.error];
  }

  get cardTooltipImageSrc() {
    if (this.cardTooltip.imageSrc && (
      this.cardTooltip.imageSrc.includes('cust_tooltips')
    )) {
      return this.cardTooltip.imageSrc;
    }
    return this.cardTooltip.imageSrc ? `${config.apiUrl}/api${this.cardTooltip.imageSrc}` : null;
  }

  get externalAccountLabel() {
    return getTranslation(`${this.platform.key}.signUpForm.externalAccountLabel`) || this.needExternalAccountField.label;
  }

  get errorDescription() {
    if (EXTERNAL_AND_DOB_EXCEPTIONS.includes(this.errorCode)) {
      const message =
        this.submittedForm && this.submittedForm.service.key === ServiceKey.TBM
          ? 'Date de naissance ou code client invalide.'
          : 'Date de naissance ou numéro client invalide.';
      return {
        dob: true,
        externalAccountId: true,
        message,
      };
    }

    if (EXTERNAL_EXCEPTIONS.includes(this.errorCode)) {
      return {
        dob: false,
        externalAccountId: true,
        message: 'Saisie invalide.',
      };
    }

    if (CLIENT_EXCEPTIONS.includes(this.errorCode)) {
      return {
        dob: true,
        externalAccountId: true,
        message: 'Mise à jour de votre compte impossible. Veuillez réessayer ultérieurement.',
      };
    }

    if (this.errorCode === IRIGO_USER_NOT_FOUND) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Compte A'Tout introuvable. Vérifiez les données saisies.`,
      };
    }

    if (this.errorCode === IRIGO_MTICKET_USER_NOT_FOUND) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Aucun compte M-Ticket n'est associé à votre compte Irigo.
          Les adresses mail de votre compte Irigo et M-Ticket doivent correspondre.`,
      };
    }

    if (this.errorCode === BIBUS_MTICKET_USER_NOT_FOUND) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Aucun compte M-Ticket n'est associé à votre compte Bibus.
          Les adresses mail de votre compte Bibus et M-Ticket doivent correspondre.`,
      };
    }

    if (this.errorCode === KORRIGO_USER_NOT_FOUND) {
      return {
        dob: true,
        externalAccountId: true,
        message: `Le service Korrigo n'a pu être associé. Vérifiez le numéro de carte et la date de naissance saisie.`,
      };
    }

    if (this.errorCode === EXTERNAL_API_DOWN) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Le service sélectionné est actuellement indisponible. Merci de réessayer ultérieurement.`,
      };
    }
    if (this.errorCode === CONFLICT_ASTUCE) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Un autre compte avec le même numéro client existe déjà.
            Un email contenant un lien permettant de finaliser la synchronisation des comptes va être envoyé sur cette adresse si vous cliquez sur le bouton ci-dessous.`,
        title: 'Numéro client déjà existant',
      };
    }
    if (this.errorCode === CONFLICT_SISMO) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Un autre compte avec le même numéro client existe déjà.
            Un email contenant un lien permettant de finaliser la synchronisation des comptes va être envoyé sur cette adresse si vous cliquez sur le bouton ci-dessous.`,
        title: 'Numéro de carte Pass Pass déjà existant',
      };
    }

    if (this.errorCode === TBM_UNAUTHORIZED_EXCEPTION) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Votre session est expiré. Veuillez vous reconnecter à TBM`,
      };
    }

    if (this.errorCode === TBM_SVD_DIFFERENCE_DETECTED_EXCEPTION) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Ce code client ne correspond pas à vos informations personnelles.
        Veuillez vous rendre sur <a href="https://mytbm.infotbm.com/" target="_blank" rel="noreferrer" >votre compte TBM</a>
        pour les mettre à jour`,
      };
    }

    if (this.errorCode === TBM_SVD_CUSTOMER_NOT_FOUND) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Code client invalide`,
      };
    }
    if (
      [TBM_PAIRING_EXCEPTION, TBM_MTICKET_PAIRING_EXCEPTION, TBM_CUSTOMER_NOT_FOUND_EXCEPTION, TBM_CONFLICT_EXCEPTION, MODALIS_CONFLICT_EXCEPTION].includes(
        this.errorCode,
      )
    ) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Une erreur est survenue, merci de réessayer. Si le problème persiste, merci de contacter notre
          <a href="https://tbmfid.infotbm.com/help" target="_blank" rel="noreferrer" >service client</a> pour obtenir de l'aide.`,
      };
    }

    if (this.errorCode === MATAWAN_USER_NOT_FOUND) {
      return {
        dob: true,
        externalAccountId: true,
        message: `Aucun compte ne correspond aux informations renseignées. Si vous êtes sûr de vos informations,
          n’hésitez pas à renouveler votre tentative ultérieurement ou à contacter le support`,
      };
    }

    if (this.errorCode === PASS_PASS_DOB_MISMATCH) {
      return {
        dob: true,
        externalAccountId: true,
        message: `La date de naissance que vous venez de renseigner, ne correspond pas à celle renseigné sur votre compte Pass Pass`,
      };
    }
    if (this.errorCode === PASS_PASS_API_MISMATCH) {
      return {
        dob: true,
        externalAccountId: true,
        message: `Aucun compte ne correspond aux informations renseignées. Si vous êtes sûr de vos informations,
          n’hésitez pas à renouveler votre tentative ultérieurement ou à contacter le support`,
      };
    }

    if (this.errorCode === TAO_DOB_MISMATCH) {
      return {
        dob: true,
        externalAccountId: true,
        message: `L'inscription a échoué, le couple numéro de carte + date de naissance ne correspond pas à un compte valide.`,
      };
    }

    if (this.errorCode) {
      return {
        dob: false,
        externalAccountId: false,
        message: `Oops! Something went wrong [${this.errorCode}]`,
      };
    }

    return undefined;
  }

  get disableServiceSelection() {
    return !getUIConfig(this.platform.key, 'signup.enableMultipleRegister');
  }

  get disableDOBInput() {
    return !getUIConfig(this.platform.key, 'signup.displayDOBInput');
  }

  get displayCGU() {
    return getUIConfig(this.platform.key, 'signup.displayCGU');
  }

  get ageRestrictionConfig() {
    return getUIConfig(this.platform.key, 'signup.ageRestriction');
  }

  dobIsValide(birthDate: dayjs.Dayjs): boolean {
    if (this.ageRestrictionConfig === AgeRestriction.MINOR_IS_RESTRICT) {
      this.underAgeErrorMessage = 'L\'inscription est impossible pour les personnes de moins de 15 ans.';
      return birthDate.isAfter(dayjs().subtract(15, 'year'));
    }

    this.underAgeErrorMessage = 'L\'inscription est impossible pour les personnes de moins de 8 ans.';
    return birthDate.isAfter(dayjs().subtract(8, 'year'));
  }


  // ===== methods =======
  createAstuceFormServices(serviceId, externalAccountId, sortedServices) {
    const myAstuceForm = {
      serviceId,
    };

    const clubAstuceService = sortedServices.find((service) => [ServiceKey.CLUB_ASTUCE].includes(service.key));
    if (!clubAstuceService) {
      throw new Error('Bad astuce services configuration');
    }

    const clubAstuceForm = {
      serviceId: clubAstuceService.id,
      externalAccountId,
    };

    return [myAstuceForm, clubAstuceForm];
  }

  createFormServices(serviceId, externalAccountId) {
    return [
      {
        serviceId,
        externalAccountId,
      },
    ];
  }

  async submit() {
    if (!(this.$refs.form as any).validate()) {
      return undefined;
    }

    const dateOfBirth: string | undefined = this.form.dateOfBirth as string | undefined;
    if (dateOfBirth) {
      const split = dateOfBirth.toString().split('/');
      const birthDate = dayjs(`${split[2]}-${split[1]}-${split[0]}`);
      if (this.dobIsValide(birthDate)) {
        this.underAgeError = true;
        return undefined;
      }
    }
    this.underAgeError = false;

    this.submitting = true;

    this.submittedForm = { ...this.form };

    const serviceId = (this.form.service as any).id;
    const externalAccountId = this.form.externalAccountId;

    const isAstuceCase = this.disableServiceSelection && externalAccountId;
    const formServices = isAstuceCase
      ? this.createAstuceFormServices(serviceId, externalAccountId, this.sortedServices)
      : this.createFormServices(serviceId, externalAccountId);

    try {
      this.errorCode = '';
      const cookieConsent = getCookieConsentements('tarteaucitron');

      if (cookieConsent && cookieConsent.facebookpixel === true && window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'Sign up',
        });
      }

      const createUserResponse = await createUser({
        services: formServices,
        platformUUID: this.platform.uuid,
        userInfos: this.userInfos,
        newsletter: this.form.newsletterIsAccepted,
        cguValidated: this.form.cguValidated,
        dateOfBirth: this.form.dateOfBirth,
        referrerCode: this.form.referrerCode,
        externalAccessToken: this.externalAccessToken,
      });

      return loginAndRedirectToLanding(createUserResponse.tokens);
    } catch (e) {
      this.userInput = {
        services: formServices,
        platformUUID: this.platform.uuid,
        userInfos: this.userInfos,
        newsletter: this.form.newsletterIsAccepted,
        cguValidated: this.form.cguValidated,
        dateOfBirth: this.form.dateOfBirth,
        referrerCode: this.form.referrerCode,
      };
      const axiosResponse = (e as any).response;
      if (axiosResponse) {
        this.errorCode = getApiErrorCode(e);
      } else {
        throw e;
      }
    } finally {
      this.submitting = false;
    }

    return undefined;
  }

  declineSynchro() {
    this.showDialogError = false;
    this.errorCode = '';
  }

  toggleInfoTooltip() {
    this.showCardTooltip = !this.showCardTooltip;
  }
}
